import { ReactNode, useCallback } from 'react'
import { MdrModule } from '@/core/modules/mdr/mdr.module'
import { useMdrCasesQuery } from '@core/hooks/useMdrCasesQuery'
import TargetIcon from '@assets/images/target-icon.svg?react'
import ShieldCheckIcon from '@assets/images/shield-check-icon.svg?react'
import GearHumanIcon from '@assets/images/gear-human-icon.svg?react'
import ChatIcon from '@assets/images/chat-icon.svg?react'
import RedDotIcon from '@assets/images/red-dot.svg?react'
import { useDrawer } from '@/core/context/DrawerContext'
import { Analytics } from '@/core/services/mixpanel'
import { AccountModule } from '@/core/modules/account/account.module'

export interface Metric {
    value: number
    label: string
    description: string
    icon: ReactNode
    variant: 'all' | 'closed' | 'inProgress' | 'waiting'
    action?: {
        text: string
        href: string
        icon?: ReactNode
    }
    testId?: string
}

export function useIncidentsOverview(timeRange: { id: string; label: string }) {
    const { setDrawerState } = useDrawer()
    const { isMdrActive } = AccountModule.useStore()
    const { data } = useMdrCasesQuery()
    const allIncidents = data
        ? MdrModule.processCases(data, timeRange)['incidents']
        : []

    // Filter incidents based on selected time range
    const incidents =
        timeRange.id === 'all'
            ? allIncidents
            : allIncidents.filter((incident) => {
                  const filterDays = parseInt(timeRange.id)
                  const cutoffDate = new Date()
                  cutoffDate.setDate(cutoffDate.getDate() - filterDays)
                  const incidentDate = new Date(incident.createdAt)
                  return incidentDate >= cutoffDate
              })

    const waitingIncidents = incidents.filter(
        (incident) => incident.status === 'waiting'
    )

    const openDrawer = useCallback(
        (row: any) => {
            Analytics.event('SEM::mdr_incident_details_drawer_open', {
                isMdrActive,
                origin: 'mdr_overview_incidents_widget',
            })
            setDrawerState((prevState: any) => ({
                ...prevState,
                isDrawerOpen: true,
                drawerContentType: 'caseDetails',
                contentProps: {
                    numberOfItems: waitingIncidents.length,
                    currentIndex: 0,
                    cases: waitingIncidents,
                    withNavigation: true,
                },
            }))
        },
        [setDrawerState, waitingIncidents, isMdrActive]
    )

    const metrics: Metric[] = [
        {
            value: incidents.length,
            label: 'Total Incidents',
            description:
                'Alerts investigated by At-Bay MDR that escalated to an Incident',
            icon: <TargetIcon />,
            variant: 'all',
            testId: 'all-incidents',
        },
        {
            value: incidents.filter((incident) => incident.status === 'closed')
                .length,
            label: 'Incidents Closed',
            description: 'Closed after MDR team investigation',
            icon: <ShieldCheckIcon />,
            variant: 'closed',
            testId: 'incidents-closed',
        },
        {
            value: incidents.filter(
                (incident) => incident.status.toLowerCase() === 'inprogress'
            ).length,
            label: 'Incidents In Progress',
            description: 'Under active investigation by MDR',
            icon: <GearHumanIcon />,
            variant: 'inProgress',
            testId: 'incidents-in-progress',
        },
        {
            value: waitingIncidents.length,
            label: 'Waiting for Your Response',
            description: 'Incident waiting for users input',
            icon: <ChatIcon />,
            variant: 'waiting',
            action: {
                text:
                    waitingIncidents.length > 1
                        ? 'View Incidents →'
                        : 'View Incident →',
                href: '#',
                icon: <RedDotIcon />,
            },
            testId: 'incidents-waiting',
        },
    ]

    return { metrics, waitingIncidents, openDrawer }
}
